import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import {
  getCommunityPostsV2InfiniteQueryOptions,
  getCommunityPostV2QueryOptions,
} from './queryOptions';
import { communityPostsSortByForQuery } from '../model/sort';
import type { EngageableLibraryPost, LibraryPost, LibraryPostPage } from '../model/types';

import type { SortOption } from '@/constants/community';

/** The first element of every Library list, page and post key. The user ID is the second. */
const LIBRARY_QUERY_ROOT = 'communityPostsV2';

/**
 * The library view a reader is in when they engage, as the screens hold it.
 * `cancerTypeId` 0 is the "All" option.
 */
export type LibraryView = {
  cancerTypeId?: number;
  searchQuery?: string;
  showSavedPosts?: boolean;
  sortBy?: SortOption;
};

export type EngagementChange =
  | { kind: 'like' }
  | {
      kind: 'save';
      /** True when the reader unsaves while looking at their saved posts. */
      removeFromSavedView: boolean;
    };

type Snapshot = {
  userId: string | undefined;
  listKey: readonly unknown[];
  postKey: readonly unknown[];
  previousList: InfiniteData<LibraryPostPage> | undefined;
  previousPost: LibraryPost | undefined;
};

/**
 * The cache keys a reader's view and post are held under. Derived here and
 * nowhere else, because an optimistic update aimed at a key the screen is not
 * reading is silently lost.
 */
function keysFor(
  userId: string | undefined,
  view: LibraryView,
  postId: number,
  postQ: string | undefined
) {
  const cancerTypeId = view.cancerTypeId && view.cancerTypeId !== 0 ? view.cancerTypeId : undefined;

  return {
    listKey: getCommunityPostsV2InfiniteQueryOptions(userId, {
      cancerTypeId,
      // Same key as the library list, which asks for the subtree.
      includeDescendants: true,
      q: view.searchQuery || undefined,
      saved: view.showSavedPosts ? true : undefined,
      sortBy: communityPostsSortByForQuery(view.showSavedPosts || undefined, view.sortBy),
    }).queryKey,
    postKey: getCommunityPostV2QueryOptions(userId, { postId, q: postQ }).queryKey,
  };
}

/** Flips the engagement on one post, keeping a like count at zero or above. */
function toggled<T extends EngageableLibraryPost>(post: T, change: EngagementChange): T {
  if (change.kind === 'save') {
    return { ...post, saved: !post.saved };
  }

  const liked = !post.liked;

  return { ...post, liked, likes: Math.max(0, (post.likes || 0) + (liked ? 1 : -1)) };
}

/**
 * Applies a like or save to every place the reader can see it before the
 * request lands: each page of the list they are in, and the post itself.
 *
 * Both flip the cached value rather than writing a target, so a double tap
 * nets out. Unsaving from the saved view removes the post from that list
 * outright, since it no longer belongs there. A list that is not cached, as
 * when saving from Research, is left alone and only the post changes.
 *
 * The returned handle restores both entries on failure and, once the request
 * settles either way, invalidates what the server may have changed.
 */
export async function applyEngagementChange(
  queryClient: QueryClient,
  {
    userId,
    postId,
    change,
    view,
    postQ,
  }: {
    userId: string | undefined;
    postId: number;
    change: EngagementChange;
    view: LibraryView;
    postQ?: string;
  }
) {
  const { listKey, postKey } = keysFor(userId, view, postId, postQ);

  await queryClient.cancelQueries({ queryKey: listKey });

  const previousList = queryClient.getQueryData<InfiniteData<LibraryPostPage>>(listKey);

  if (previousList) {
    const removing = change.kind === 'save' && change.removeFromSavedView;

    queryClient.setQueryData<InfiniteData<LibraryPostPage>>(listKey, {
      ...previousList,
      pages: previousList.pages.map((page) => ({
        ...page,
        posts: removing
          ? page.posts.filter((post) => Number(post.id) !== postId)
          : page.posts.map((post) => (Number(post.id) === postId ? toggled(post, change) : post)),
      })),
    });
  }

  await queryClient.cancelQueries({ queryKey: postKey });

  const previousPost = queryClient.getQueryData(postKey);

  if (previousPost) {
    queryClient.setQueryData(postKey, toggled(previousPost, change));
  }

  const snapshot: Snapshot = { userId, listKey, postKey, previousList, previousPost };

  return {
    rollback: () => rollbackEngagementChange(queryClient, snapshot),
    settle: () => settleEngagementChange(queryClient, snapshot, change),
  };
}

function rollbackEngagementChange(queryClient: QueryClient, snapshot: Snapshot) {
  queryClient.setQueryData(snapshot.listKey, snapshot.previousList);

  queryClient.setQueryData(snapshot.postKey, snapshot.previousPost);
}

/**
 * A save can move a post into or out of the saved view, which is a list the
 * reader is not looking at, so it refreshes every library list. A like changes
 * only counts, so it refreshes just the list and post on screen.
 */
function settleEngagementChange(
  queryClient: QueryClient,
  snapshot: Snapshot,
  change: EngagementChange
) {
  void queryClient.invalidateQueries({ queryKey: snapshot.listKey, exact: false });

  if (change.kind === 'save') {
    void queryClient.invalidateQueries({
      queryKey: [LIBRARY_QUERY_ROOT, snapshot.userId],
      exact: false,
    });
  }

  void queryClient.invalidateQueries({ queryKey: snapshot.postKey });
}

/**
 * A new comment changes the post's comment count, which the post and the lists
 * show, so both are refreshed. Comments call this through a callback the post
 * screen hands them rather than importing the Library's cache keys.
 *
 * It refreshes the post under every search it was read with, and every list
 * and page, whatever the filters. Naming one list instead would have to rebuild
 * its key exactly, and a key built with one param off matches nothing: that is
 * how the composer's refresh used to miss the default list.
 */
export function refreshPostAfterComment(
  queryClient: QueryClient,
  userId: string | undefined,
  postId: number
) {
  void queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      queryKey[0] === LIBRARY_QUERY_ROOT &&
      queryKey[1] === userId &&
      (queryKey[2] === postId || typeof queryKey[2] !== 'number'),
  });
}
